import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import {
  requestAiProductInfoStatus,
  requestTriggerAiProductInfo
} from "./aiProductInfo.request";
import {
  AiProductInfoStatusResponse,
  TriggerAiProductInfoBody
} from "./aiProductInfo.types";

const FAST_POLL_INTERVAL_MS = 2_000;
const SLOW_POLL_INTERVAL_MS = 5_000;
const FAST_POLL_WINDOW_MS = 30_000;
const HARD_TIMEOUT_MS = 3 * 60_000;

/**
 * Drives the two-step AI product-info workflow:
 *   1. POST trigger → grab a workflowId.
 *   2. GET status on a polling cadence until the workflow reaches a
 *      terminal state (`completed` / `failed`) or we hit a hard cap.
 *
 * Callers read `result.content` only when `isCompleted` flips to true and
 * call `reset()` between runs so a stale workflowId doesn't leak between
 * generations.
 */
export const useAiProductInfo = () => {
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  const trigger = useMutation({
    mutationFn: requestTriggerAiProductInfo,
    onSuccess: (data) => {
      setWorkflowId(data.workflowId);
      setStartedAt(Date.now());
    }
  });

  const status = useQuery<AiProductInfoStatusResponse>({
    queryKey: ["aiProductInfo", workflowId],
    queryFn: () => requestAiProductInfoStatus(workflowId as string),
    enabled: !!workflowId,
    refetchInterval: (data) => {
      if (!data) return FAST_POLL_INTERVAL_MS;
      if (data.status !== "pending") return false;

      const elapsed = startedAt ? Date.now() - startedAt : 0;
      if (elapsed > HARD_TIMEOUT_MS) return false;
      return elapsed < FAST_POLL_WINDOW_MS
        ? FAST_POLL_INTERVAL_MS
        : SLOW_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false
  });

  const reset = useCallback(() => {
    setWorkflowId(null);
    setStartedAt(null);
    trigger.reset();
  }, [trigger]);

  const elapsedMs = startedAt ? Date.now() - startedAt : 0;
  const isTimedOut =
    !!workflowId && status.data?.status === "pending" && elapsedMs > HARD_TIMEOUT_MS;

  const isPolling =
    !!workflowId &&
    (status.data?.status === "pending" || (!status.data && !status.isError));

  return {
    triggerGeneration: useCallback(
      (body: TriggerAiProductInfoBody) => trigger.mutate(body),
      [trigger]
    ),
    isTriggering: trigger.isPending,
    triggerError: trigger.error,
    isPolling,
    isCompleted: status.data?.status === "completed",
    isFailed: status.data?.status === "failed" || isTimedOut,
    result: status.data?.status === "completed" ? status.data.content : null,
    elapsedMs,
    reset
  };
};
